import { useContext, useState } from "react";
import UiContext from "@store/ui-context";
import useAPIRequest from "@custom_hooks/simple/useAPIRequest";
import { format, subMonths } from "date-fns";
import { id as idLocale } from "date-fns/locale";

const formatLiter = (value) =>
  `${Number(value).toLocaleString("id-ID", { maximumFractionDigits: 2 })} L`;

// keterangan of the stok awal of a month that has no line yet: the counted stock of this month
// and the sisa of the month before
export const buildKeteranganStokAwal = ({ tanggal_waktu, stok_fisik, saldo }) => {
  if (!tanggal_waktu || stok_fisik === null || stok_fisik === undefined || saldo === null) {
    return "";
  }

  const date = new Date(
    typeof tanggal_waktu === "string" ? tanggal_waktu.replace(" ", "T") : tanggal_waktu
  );
  if (isNaN(date)) return "";

  const bulanIni = format(date, "MMMM yyyy", { locale: idLocale });
  const bulanLalu = format(subMonths(date, 1), "MMMM yyyy", { locale: idLocale });

  return `Stok awal ${bulanIni} sebesar ${formatLiter(
    stok_fisik
  )} (hasil cek fisik). Sisa akhir ${bulanLalu} di sistem: ${formatLiter(saldo)}.`;
};

export const useAPIEditKartuStokMinyak = (props) => {
  const { onConfirmed } = props;

  const uiContext = useContext(UiContext);
  const { requestGet, requestGetOne, requestPost, loading } = useAPIRequest();

  const [data, setData] = useState({});
  const [error, setError] = useState({});
  const [saldo, setSaldo] = useState(null);

  // "masuk" = stock added (stok awal of the month), otherwise fuel out to an alat berat
  const isMasuk = data.jenis_mutasi === "masuk";
  // "awal" = the stock of the first day of the month, counted by the PNJ
  const isAwal = data.mode === "awal";
  // counted stock minus the sisa of the system (null until the sisa is known)
  const selisihAwal =
    isAwal && saldo !== null ? Number(data.stok_fisik ?? 0) - Number(saldo) : null;

  //  API call section

  const fetchData = async ({ id, extraData }) => {
    return requestGetOne({
      fullUrl: `/api/transaksi/kartustokminyak/one/${id}?with_department=1&with_jenis_minyak=1&with_alat_berat=1&with_operator=1`,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data: _data, message }) => {
        const newData = reformatDataAfterFetch(_data);
        if (extraData) {
          setData({ ...extraData, ...newData });
        } else {
          setData(newData);
        }
      },
    });
  };

  // the latest balance (sisa) of the department + jenis minyak, used for the warning
  const fetchSaldo = async ({ department_id, jenis_minyak_id }) => {
    if (!department_id || !jenis_minyak_id) {
      setSaldo(null);
      return;
    }

    return requestGet({
      fullUrl: `/api/transaksi/kartustokminyak/data`,
      params: {
        page: 1,
        take: 1,
        filter: `department_id:=:${department_id};jenis_minyak_id:=:${jenis_minyak_id}`,
        order: "tanggal_waktu",
        order_method: "DESC",
      },
      onError: ({ message, data }) => setSaldo(null),
      onSuccess: ({ data: _data }) => {
        setSaldo(_data && _data.length > 0 ? Number(_data[0].sisa ?? 0) : 0);
      },
    });
  };

  const saveData = async () => {
    setError({});

    return requestPost({
      fullUrl: `/api/transaksi/kartustokminyak/save`,
      body: reformatDataBeforeSave(data),
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
        if (data) setError(data);
      },
      onSuccess: ({ data: _data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        const newData = reformatDataAfterFetch(_data);
        // the response may not carry the relations, keep the ones already known
        onConfirmed({
          data_department: data.data_department,
          data_jenis_minyak: data.data_jenis_minyak,
          data_alat_berat: data.data_alat_berat,
          data_operator: data.data_operator,
          ...newData,
        });
      },
    });
  };

  // other function section

  const validate = () => {
    let valid = true;
    let newError = {};

    if (!data.department_id || data.department_id <= 0) {
      newError = { ...newError, department_id: "Department harus dipilih" };
      valid = false;
    }

    if (!data.jenis_minyak_id || data.jenis_minyak_id <= 0) {
      newError = { ...newError, jenis_minyak_id: "Jenis minyak harus dipilih" };
      valid = false;
    }

    if (!data.tanggal_waktu) {
      newError = { ...newError, tanggal_waktu: "Tanggal tidak boleh kosong" };
      valid = false;
    }

    if (isAwal) {
      if (data.stok_fisik === null || data.stok_fisik === undefined || data.stok_fisik < 0) {
        newError = { ...newError, stok_fisik: "Isi stok fisik hasil pengecekan" };
        valid = false;
      } else if (saldo === null) {
        newError = { ...newError, stok_fisik: "Sisa di sistem belum terbaca" };
        valid = false;
      } else if (selisihAwal === 0 && !data.sheet_kosong) {
        // the first line of a month is always recorded, even when the count equals last month's sisa
        newError = {
          ...newError,
          stok_fisik: `Sama dengan sisa di sistem (${saldo.toLocaleString(
            "id-ID"
          )} L), tidak perlu dicatat`,
        };
        valid = false;
      }
    } else if (isMasuk) {
      if (!data.masuk || data.masuk <= 0) {
        newError = { ...newError, masuk: "Volume masuk harus lebih dari 0" };
        valid = false;
      }
    } else {
      if (!data.alat_berat_id || data.alat_berat_id <= 0) {
        newError = { ...newError, alat_berat_id: "Alat berat harus dipilih" };
        valid = false;
      }

      if (!data.operator_nama || data.operator_nama.trim() === "") {
        newError = { ...newError, operator_id: "Operator harus dipilih" };
        valid = false;
      }

      if (!data.keluar || data.keluar <= 0) {
        newError = { ...newError, keluar: "Volume keluar harus lebih dari 0" };
        valid = false;
      } else if (saldo !== null && !(data.id > 0) && data.keluar > saldo) {
        newError = {
          ...newError,
          keluar: `Volume keluar melebihi sisa stok (${saldo.toLocaleString(
            "id-ID"
          )} L)`,
        };
        valid = false;
      }
    }

    setError(newError);
    return valid;
  };

  // only the fields of the API payload (without the nested relations);
  // stock added (stok awal / tambah stok) has no alat berat and no operator
  const reformatDataBeforeSave = (_data) => {
    // stok awal: the PNJ counted the stock. Only the difference with the sisa of the system is
    // recorded (more = masuk, less = keluar), so the sisa of the sheet becomes the counted stock.
    // (no difference = a masuk line of 0 L, which marks the stok awal of the month)
    const awalMasuk = isAwal ? selisihAwal >= 0 : isMasuk;

    const body = {
      id: data.id,
      department_id: data.department_id,
      jenis_minyak_id: data.jenis_minyak_id,
      tanggal_waktu: data.tanggal_waktu,
      jenis_mutasi: awalMasuk ? "masuk" : "keluar",
      keterangan: data.keterangan ?? "",
    };

    if (isAwal) {
      if (awalMasuk) {
        body.masuk = selisihAwal;
      } else {
        body.keluar = -selisihAwal;
      }
    } else if (isMasuk) {
      body.masuk = data.masuk;
    } else {
      body.alat_berat_id = data.alat_berat_id;
      body.operator_id = data.operator_id ?? null;
      body.operator_nama = data.operator_nama;
      body.keluar = data.keluar;
    }

    return body;
  };

  const reformatDataAfterFetch = (_data) => {
    return _data;
  };

  const fetchDataOrCreateNew = ({ id, extraData }) => {
    setSaldo(null);
    if (id && id > 0) {
      fetchData({ id, extraData });
    } else {
      if (extraData) {
        setData({ ...generateNewData(), ...extraData });
      } else {
        setData(generateNewData());
      }
    }
  };

  const generateNewData = () => {
    return {
      tanggal_waktu: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      jenis_mutasi: "keluar",
      masuk: 0,
      keluar: 0,
      keterangan: "",
    };
  };

  // handle ui event section

  const handleOnConfirm = () => {
    const valid = validate();
    if (valid) {
      saveData();
    }
  };

  return {
    loading,
    data,
    setData,
    error,
    setError,
    saldo,
    isMasuk,
    isAwal,
    selisihAwal,
    fetchSaldo,
    fetchDataOrCreateNew,
    handleOnConfirm,
    onConfirmed,
  };
};
